'use client'

import React, { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'

type Testimonial = {
  text: string
  name: string
  role: string
  initials?: string
}

function Avatar({ name, initials }: { name: string; initials?: string }) {
  const displayInitials = initials || name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
      {displayInitials}
    </div>
  )
}

export const TestimonialsColumn = ({
  className,
  testimonials,
  duration = 10,
}: {
  className?: string
  testimonials: Testimonial[]
  duration?: number
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  // Detect mobile and reduced motion on mount
  useEffect(() => {
    setIsMobile(
      window.innerWidth < 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    )
    setReducedMotion(
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
  }, [])

  // IntersectionObserver to pause animation when off-screen
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.1 }
    )

    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // Don't animate if user prefers reduced motion
  const shouldAnimate = isVisible && !reducedMotion

  return (
    <div ref={containerRef} className={className}>
      <motion.div
        animate={shouldAnimate ? {
          translateY: '-50%',
        } : {}}
        transition={{
          duration: duration,
          repeat: Infinity,
          ease: 'linear',
          repeatType: 'loop',
        }}
        className="flex flex-col gap-4 pb-4 sm:gap-6 sm:pb-6"
        style={reducedMotion ? { translateY: 0 } : undefined}
      >
        {[...new Array(2)].map((_, index) => (
          <React.Fragment key={index}>
            {testimonials.map(({ text, name, role, initials }, i) => (
              <div
                key={i}
                className={`p-6 sm:p-10 rounded-3xl border border-purple-500/20 dark:border-white/10 bg-white/85 dark:bg-white/5 shadow-lg shadow-purple-500/10 max-w-xs w-full ${isMobile ? '' : 'backdrop-blur-sm'}`}
              >
                <div className="text-indigo-950/80 dark:text-white/80">{text}</div>
                <div className="flex items-center gap-2 mt-5">
                  <Avatar name={name} initials={initials} />
                  <div className="flex flex-col">
                    <div className="font-medium tracking-tight leading-5 text-indigo-950 dark:text-white">
                      {name}
                    </div>
                    <div className="leading-5 opacity-60 tracking-tight text-indigo-950 dark:text-white">
                      {role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  )
}
